// StockOutEditModal.jsx - FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import BatchDropdown from '../components/BatchDropdown'; 

const customStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    backgroundColor: "white",
    color: "black",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#ddd" : "white",
    color: "black",
  }),
};

function initItem() {
  return {
    medID: null,
    medicineName: "",
    medicinecomposition: "",
    category: "",
    companyName: "",
    packSize: "",
    hsnCode: "",
    batchNo: "",
    exp: "",
    availableQty: 0,
    leaf: 0,
    piece: 0,
    qty: 0,
  };
}



const StockOutEditModal = ({ show, stockOutData, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [type, setType] = useState("EXPIRED");
  
  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  
  const [currentItem, setCurrentItem] = useState(initItem());
  const [items, setItems] = useState([]);

  const formRef = useRef(null);
  const medicineSelectRef = useRef(null);
  const batchDropdownRef = useRef(null);
const leafInputRef = useRef(null);
const addButtonRef = useRef(null);


  // ✅ Initial focus on modal open
  useEffect(() => {
    if (show) {
      setTimeout(() => {
        const firstInput = formRef.current?.querySelector(
          'select, input:not([type="hidden"]), .react-select__input input'
        );
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }
  }, [show]);

  // ✅ Load stock out data on mount
  useEffect(() => {
    if (show && stockOutData) {
      loadStockOutData(stockOutData);
    }
  }, [show, stockOutData]);

  // ✅ FIXED - Remove duplicate packSize line
  const loadStockOutData = (data) => {
    setType(data.type || "EXPIRED");
    
    setItems(
      data.items?.map((item) => ({
        medID: item.medID,
        medicineName: item.medicine,
        medicinecomposition: item.mediInUnit || "",
        category: item.category || "",
        companyName: item.brand || "",
        packSize: item.packSize || "",  // ✅ FIXED - Only one line now
        hsnCode: item.hsncode || "",
        batchNo: item.batchNo,
        exp: item.exp || "",
        availableQty: 0,
        leaf: item.leaf || 0,
        piece: item.piece || 0,
        qty: item.qty || 0,
      })) || []
    );

  
    setError("");
    setSuccess("");
  };

  // ✅ Reset form on modal close
  useEffect(() => {
    if (!show) {
      setCurrentItem(initItem());
      setBatchOptions([]);
      setLoadingBatches(false);
      setItems([]);
      setError("");
      setSuccess("");
      setType("EXPIRED");
    }
  }, [show]);

  // ✅ Keyboard navigation
  useEffect(() => {
    if (!show) return;
    
    const handler = (e) => {
      if (formRef.current && e.target.closest("form") === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [show]);

  // Medicine search
  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 1) return [];

    try {
     const res = await customFetch(
        `/MedicineDT/search?medicine=${encodeURIComponent(inputValue.trim())}`
      );
      if (!res.ok) return [];
      const data = await res.json();

      return data.map((m) => ({
        value: m.medID,
        label: m.medicine,
        data: m,
      }));
    } catch {
      return [];
    }
  };

  const loadBatchNumbers = async (medID) => {
    if (!medID) return [];

    setLoadingBatches(true);
    try {
    const res = await customFetch(`/MedicineBatch/batches/${medID}?hid=1&frid=1`);

      if (!res.ok) {
        setBatchOptions([]);
        return [];
      }

      const data = await res.json();
      const options = data.map((batch) => ({
        value: batch.batchNo,
        label: `${batch.batchNo} | Avail: ${batch.balance}`,
        balance: batch.balance,
        exp: batch.expDate || "",
        mrp: batch.mrp,        // ✅ ADD
        sRate: batch.sRate,    // ✅ ADD
      }));

      setBatchOptions(options);
      return data;
    } catch (error) {
      console.error("Error loading batches:", error);
      setBatchOptions([]);
      return [];
    } finally {
      setLoadingBatches(false);
    }
  };


  const handleMedicineSelect = async (selected) => {
    if (!selected) {
      setCurrentItem(initItem());
      setBatchOptions([]);
      return;
    }

    const m = selected.data;
    setCurrentItem({
      ...initItem(),
      medID: m.medID,
      medicineName: m.medicine,
      medicinecomposition: m.mediInUnit || "",
      category: m.categoryName || "",
      companyName: m.brandName || "",
      packSize: m.iUnit || "",
      hsnCode: m.hsncode || "",
    });

    await loadBatchNumbers(m.medID);
    
    // ✅ CHANGE: batchSelectRef -> batchDropdownRef
    setTimeout(() => {
      if (batchDropdownRef.current) {
        batchDropdownRef.current.focus();
      }
    }, 200);
  };

// ✅ ADD THIS
const handleBatchSelectCustom = (batch) => {
    if (!batch) {
      setCurrentItem((prev) => ({ 
        ...prev, 
        batchNo: "", 
        availableQty: 0,
        exp: ""
      }));
      return;
    }

    setCurrentItem((prev) => ({
      ...prev,
      batchNo: batch.value,
      availableQty: batch.balance,
      exp: batch.exp || "",
    }));
    
    // ✅ Focus Leaf field
    setTimeout(() => {
      if (leafInputRef.current) {
        leafInputRef.current.focus();
        leafInputRef.current.select();
      }
    }, 100);
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    const updatedItem = { ...currentItem, [name]: value };
    
    const packSize = parseFloat(updatedItem.packSize) || 1;
    const leaf = parseFloat(updatedItem.leaf) || 0;
    const piece = parseFloat(updatedItem.piece) || 0;
    updatedItem.qty = leaf * packSize + piece;
    
    setCurrentItem(updatedItem);
  };

  const handleAddItem = () => {
    if (!currentItem.medID) {
      setError("Please select a medicine.");
      return;
    }
    if (!currentItem.batchNo) {
      setError("Please select a batch number.");
      return;
    }
    if (Number(currentItem.qty) <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }
    if (Number(currentItem.qty) > Number(currentItem.availableQty)) {
      setError("Quantity cannot exceed available quantity.");
      return;
    }

    setError("");
    
    setItems((prev) => [{ ...currentItem }, ...prev]);
    setCurrentItem(initItem());
    setBatchOptions([]);
    
    setTimeout(() => {
      if (medicineSelectRef.current) {
        medicineSelectRef.current.focus();
      }
    }, 150);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditRowToForm = async (index) => {
    const row = items[index];
    if (!row) return;

    setItems((prev) => prev.filter((_, i) => i !== index));

    if (row.medID) {
      const batchData = await loadBatchNumbers(row.medID);
      const matchingBatch = batchData.find((b) => b.batchNo === row.batchNo);
      
      setCurrentItem({
        medID: row.medID,
        medicineName: row.medicineName,
        medicinecomposition: row.medicinecomposition,
        category: row.category,
        companyName: row.companyName,
        packSize: row.packSize,
        hsnCode: row.hsnCode,
        batchNo: row.batchNo,
        exp: matchingBatch ? matchingBatch.expDate || "" : row.exp,
        availableQty: matchingBatch ? matchingBatch.balance : 0,
        leaf: row.leaf,
        piece: row.piece,
        qty: row.qty,
      });
    } else {
      setCurrentItem(row);
    }
    
    setError("");
    setSuccess("");
    
    // ✅ Focus leaf input
    setTimeout(() => {
      if (leafInputRef.current) {
        leafInputRef.current.focus();
        leafInputRef.current.select();
      }
    }, 250);
  };


  // ✅ Keyboard navigation
  const handleKeyDown = (e) => {
    const isInsideAsyncSelect = 
      e.target.closest('.react-select__control') ||
      e.target.classList?.contains('react-select__input');
    
    if (isInsideAsyncSelect && e.key === 'Enter') {
      return;
    }

    const isReactSelectMenuOpen = document.querySelector('.react-select__menu');
    if (isReactSelectMenuOpen) return;


       const activeBatchDropdown = e.target.closest('[data-batch-dropdown="true"]');
    if (activeBatchDropdown) return;

    const batchPanelOpen = document.querySelector('.batch-dropdown-panel');
    if (batchPanelOpen) return;

    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]):not([tabindex="-1"]), ' +
      '.react-select__input input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'button:not([disabled]):not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    const isAddButton = 
      e.target.textContent?.includes("Add Item") ||
      e.target.classList?.contains('add-item-btn');

    if (e.key === "Tab") {
      return;
    }

    if (e.key === "Enter") {
      if (isAddButton) {
        e.preventDefault();
        handleAddItem();
        return;
      }

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        e.target.closest("form").requestSubmit();
        return;
      }

      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    }
  };

  // ✅ FIXED - Update stock out WITH PACKSIZE
  const handleUpdateStockOut = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      setError("At least add one item.");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
    

      const payload = {
        type,
        items: items.map((it) => ({
          medID: it.medID,
          medicineName: it.medicineName,
          composition: it.medicinecomposition,
          brand: it.companyName,
          category: it.category,
          packSize: parseInt(it.packSize) || null,  // ✅ ADDED - Send PackSize
          batchNo: it.batchNo,
          exp: it.exp || null,
          leaf: parseInt(it.leaf) || 0,
          piece: parseInt(it.piece) || 0,
          qty: it.qty,
          hsnCode: it.hsnCode || "",
        })),
      };

      
const res = await customFetch(
        `/StockOut/update/${encodeURIComponent(stockOutData.recId)}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to update stock out.");
      }

      const data = await res.json();

      setSuccess(
        `✅ Stock out updated! RecId: ${data.recId} | Old Type: ${data.oldType} → New Type: ${data.newType} | Items: ${data.itemCount} | Total Qty: ${data.totalQty} Pcs`
      );

      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
      <div className="container mt-4">
        <div className="card shadow">
          <div className="card-header bg-warning py-2">
            <div className="row">
              <h6 className="col-6">
                ✏️ Edit Stock Out - {stockOutData?.recId}
                <span className="badge bg-danger ms-2">{items.length} Items</span>
              </h6>
              <div className="col-6 text-end">
                <button type="button" className="btn-close" onClick={onClose}></button>
              </div>
            </div>
          </div>

          <form ref={formRef} onSubmit={handleUpdateStockOut}>
            <div className="modal-body p-3 fs-7">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              {success && <div className="alert alert-success py-2">{success}</div>}

              {/* Type Section */}
              <div className="border p-2 mb-1 rounded bg-light">
                <div className="row mb-0">
                  <div className="col-md-3">
                    <label className="form-label small fw-bold mt-0 mb-0">Rec ID</label>
                    <input
                      className="form-control form-control-sm bg-light"
                      value={stockOutData?.recId}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mt-0 mb-0">Rec No</label>
                    <input
                      className="form-control form-control-sm bg-light"
                      value={stockOutData?.recNo}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mt-0 mb-0">
                      Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value="EXPIRED">Expired</option>
                      <option value="DAMAGED">Damaged</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Medicine Search Section */}
              <div className="border p-2 mb-1 rounded">
                <div className="row mb-1">
                  <div className="col-md-4">
                    <label className="form-label small mt-0 mb-0">
                      💊 Search Medicine <span className="text-danger">*</span>
                    </label>
                    
                    <div onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const menu = document.querySelector('.react-select__menu');
                        if (menu) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        if (!currentItem.medID && items.length > 0) return;
                        if (currentItem.medID) return;
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}>
                      <AsyncSelect
                        ref={medicineSelectRef}
                        cacheOptions
                        loadOptions={loadMedicineOptions}
                        onChange={handleMedicineSelect}
                        placeholder="Start typing medicine name..."
                        isClearable
                        styles={customStyles}
                        value={
                          currentItem.medID
                            ? {
                                value: currentItem.medID,
                                label: currentItem.medicineName,
                              }
                            : null
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label small mt-0 mb-0">Composition</label>
                    <input
                      className="form-control form-control-sm"
                      value={currentItem.medicinecomposition}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mt-0 mb-0">Category</label>
                    <input
                      className="form-control form-control-sm"
                      value={currentItem.category}
                      readOnly
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label small mt-0 mb-0">Company</label>
                    <input
                      className="form-control form-control-sm"
                      value={currentItem.companyName}
                      readOnly
                    />
                  </div>

                  <div className="col-md-1">
                    <label className="form-label small mt-0 mb-0">Pack Size</label>
                    <input
                      className="form-control form-control-sm"
                      value={currentItem.packSize}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row g-2 mb-1">
                  <div className="col-md-2">
                    <label className="form-label small mt-0 mb-0">HSN Code</label>
                    <input
                      className="form-control form-control-sm"
                      value={currentItem.hsnCode}
                      readOnly
                    />
                  </div>

                  {currentItem.medID && (
                    <>
                      <div className="col-md-2">
                        <label className="form-label small mt-0 mb-0">
                          Batch No <span className="text-danger">*</span>
                        </label>
                       <BatchDropdown
    ref={batchDropdownRef}
    options={batchOptions}
    value={currentItem.batchNo}
    onChange={handleBatchSelectCustom}
    loading={loadingBatches}
    disabled={batchOptions.length === 0}
/>

                      </div>

                      <div className="col-md-2">
                        <label className="form-label small mt-0 mb-0">Expiry Date</label>
                        <input
                          className="form-control form-control-sm bg-warning"
                          value={
                            currentItem.exp 
                              ? new Date(currentItem.exp).toLocaleDateString('en-GB')
                              : ""
                          }
                          readOnly
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label small mt-0 mb-0">Available Qty</label>
                        <input
                          className="form-control form-control-sm bg-warning"
                          value={currentItem.availableQty}
                          readOnly
                        />
                      </div>
                    </>
                  )}
                </div>

                {currentItem.batchNo && (
                  <div className="row g-2 mb-1">
                    <div className="col-md-1">
                      <label className="form-label small mt-0 mb-0">
                        Leaf <span className="text-danger">*</span>
                      </label>
                      <input
                        ref={leafInputRef}
                        className="form-control form-control-sm"
                        name="leaf"
                        value={currentItem.leaf}
                        onChange={handleItemChange}
                      />
                    </div>

                    <div className="col-md-1">
                      <label className="form-label small mt-0 mb-0">
                        Piece <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control form-control-sm"
                        name="piece"
                        value={currentItem.piece}
                        onChange={handleItemChange}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label small mt-0 mb-0">Total Qty (Pcs)</label>
                      <input
                        className="form-control form-control-sm bg-success text-white fw-bold"
                        value={currentItem.qty}
                        readOnly
                      />
                    </div>

                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        ref={addButtonRef}
                        type="button"
                        className="btn btn-danger btn-sm w-100 add-item-btn"
                        onClick={(e) => {  // ✅ UPDATE
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddItem();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();  // ✅ ADD
                            handleAddItem();
                          }
                        }}
                      >
                        ➕ Add Item
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Items Table */}
              <div className="table-responsive mb-1" style={{ fontSize: "0.8rem" }}>
                <table className="table table-sm table-bordered table-hover align-middle text-center">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Medicine</th>
                      <th>Category</th>
                      <th>Company</th>
                      <th>Batch</th>
                      <th>Expiry Date</th>
                      <th>Leaf</th>
                      <th>Piece</th>
                      <th>Qty (Pcs)</th>
                      <th>HSN Code</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-muted">No items added.</td>
                      </tr>
                    ) : (
                      items.map((it, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td className="text-start">{it.medicineName}</td>
                          <td>{it.category}</td>
                          <td>{it.companyName}</td>
                          <td>{it.batchNo}</td>
                          <td className="bg-warning">
                            {it.exp 
                              ? new Date(it.exp).toLocaleDateString('en-GB')
                              : "-"}
                          </td>
                          <td>{it.leaf}</td>
                          <td>{it.piece}</td>
                          <td className="fw-bold">{it.qty}</td>
                          <td>{it.hsnCode}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary me-1"
                              onClick={() => handleEditRowToForm(idx)}
                              title="Edit"
                              tabIndex="-1"
                            >
                              ✎
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRemoveItem(idx)}
                              title="Delete"
                              tabIndex="-1"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {items.length > 0 && (
                <div className="alert alert-info py-2">
                  <strong>Total Items:</strong> {items.length} | 
                  <strong className="ms-3">Total Quantity:</strong> {items.reduce((sum, it) => sum + it.qty, 0)} Pcs
                </div>
              )}
            </div>

            <div className="modal-footer py-2">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm px-4"
                disabled={loading || items.length === 0}
              >
                {loading ? "💾 Updating..." : "💾 Update Stock Out"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StockOutEditModal;
